// HistoryDrawer — lists every revision of a card, lets the user expand any
// rev to see its snapshot + a unified diff against the previous rev, and
// restore an old rev as the current state.
//
// Why a drawer (not a modal): editing the card and looking at its history
// are common adjacent flows; a slide-in panel that doesn't take over the
// screen lets the user cross-reference. Lives next to CardPeek (the peek
// hosts the toggle button) so spatial position stays predictable.
//
// Default filtering:
//   - fs_sync revisions hidden by default (vim/Obsidian touches with no
//     content change still surface a row, but `extra-only` rows are also
//     filtered separately).
//   - extra-only revs (only `dismissed_gaps` etc. changed) are hidden by
//     default — they're audit data, not real edits.
// Both are togglable via the filter bar at the top.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type CardHistoryRevision } from '../../api';
import { lineDiff, isExtraOnlyChange, type DiffSegment } from '../../diff';
import { ConfirmDialog } from '../ConfirmDialog';
import i18n from '../../i18n';

export interface HistoryDrawerProps {
  projectId: string;
  cardId: string;
  /** Close button + outside click handler. The hosting CardPeek owns the
   *  open/closed state; this component is purely controlled. */
  onClose: () => void;
  /** After a successful restore the parent should re-fetch the card so the
   *  current state in CardPeek matches what's now in the DB. Optional — if
   *  omitted, restore is hidden. */
  onRestored?: () => void;
}

export function HistoryDrawer({
  projectId,
  cardId,
  onClose,
  onRestored,
}: HistoryDrawerProps) {
  const { t } = useTranslation();
  const [revs, setRevs] = useState<CardHistoryRevision[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRev, setExpandedRev] = useState<number | null>(null);
  const [showFs, setShowFs] = useState(false);
  const [showExtraOnly, setShowExtraOnly] = useState(false);
  const [restoringRev, setRestoringRev] = useState<number | null>(null);
  const [confirmRestoreRev, setConfirmRestoreRev] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .listCardHistory(projectId, cardId)
      .then((rs) => {
        if (cancelled) return;
        setRevs(rs);
        // Auto-expand the latest non-current rev for quick visual feedback —
        // skip rev 1 if there's only one (nothing to diff against).
        if (rs.length >= 2) setExpandedRev(rs[0].revision);
      })
      .catch((e) => {
        if (cancelled) return;
        setError((e as Error).message);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, cardId]);

  // Pre-compute prev rev per revision so the diff renderer doesn't re-walk
  // the array. Index by revision number → previous rev (or null on rev 1).
  const prevByRev = useMemo(() => {
    const map = new Map<number, CardHistoryRevision | null>();
    for (let i = 0; i < revs.length; i++) {
      const next = revs[i + 1] ?? null;
      map.set(revs[i].revision, next);
    }
    return map;
  }, [revs]);

  // Apply default filters. Extra-only filtering compares against the prev
  // rev — if there's no prev (rev 1), the row is by definition not "only
  // extra changed" so we keep it.
  const visibleRevs = useMemo(
    () =>
      revs.filter((r) => {
        if (!showFs && r.op === 'fs_sync') return false;
        if (!showExtraOnly) {
          const prev = prevByRev.get(r.revision);
          if (prev && isExtraOnlyChange(prev, r)) return false;
        }
        return true;
      }),
    [revs, showFs, showExtraOnly, prevByRev],
  );

  const hiddenCount = revs.length - visibleRevs.length;
  const isCurrent = (r: CardHistoryRevision) =>
    revs.length > 0 && r.revision === revs[0].revision;

  const onRestore = async (revision: number) => {
    setRestoringRev(revision);
    try {
      await api.restoreCardRevision(projectId, cardId, revision);
      // Reload list — the restore itself is recorded as a new rev.
      const rs = await api.listCardHistory(projectId, cardId);
      setRevs(rs);
      setExpandedRev(rs[0]?.revision ?? null);
      onRestored?.();
    } catch (e) {
      setError(`restore failed: ${(e as Error).message}`);
    } finally {
      setRestoringRev(null);
      setConfirmRestoreRev(null);
    }
  };

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        top: 0,
        right: 0,
        bottom: 0,
        width: 'min(560px, 92vw)',
        background: 'white',
        borderLeft: '1px solid var(--rule)',
        boxShadow: '-12px 0 32px rgba(0,0,0,0.12)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1600,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 16px',
          borderBottom: '1px solid var(--rule)',
          background: 'var(--paper-0)',
          flexShrink: 0,
        }}
      >
        <span
          className="mono"
          style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: 'var(--ink-0)' }}
        >
          {t('history.title')} · {cardId}
        </span>
        <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>
          {t('history.revCount', { count: revs.length })}
          {hiddenCount > 0 && ` · ${t('history.hiddenCount', { count: hiddenCount })}`}
        </span>
        <span style={{ flex: 1 }} />
        <FilterChip
          label={t('history.filterFs')}
          active={showFs}
          onToggle={() => setShowFs((v) => !v)}
          title={t('history.filterFsTooltip')}
        />
        <FilterChip
          label={t('history.filterMetadata')}
          active={showExtraOnly}
          onToggle={() => setShowExtraOnly((v) => !v)}
          title={t('history.filterMetadataTooltip')}
        />
        <button
          onClick={onClose}
          aria-label={t('history.closeAria')}
          className="mono"
          style={{
            fontSize: 14,
            lineHeight: 1,
            padding: '2px 8px',
            border: 'none',
            background: 'transparent',
            color: 'var(--ink-3)',
            cursor: 'pointer',
          }}
        >
          ×
        </button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 4px 12px' }}>
        {loading && (
          <div style={{ padding: '20px 16px', color: 'var(--ink-3)', fontSize: 12 }}>
            {t('common.loading')}
          </div>
        )}
        {error && (
          <div
            style={{
              padding: '20px 16px',
              color: 'var(--red)',
              fontSize: 12,
              fontFamily: 'JetBrains Mono, monospace',
            }}
          >
            {error}
          </div>
        )}
        {!loading && !error && visibleRevs.length === 0 && (
          <div style={{ padding: '20px 16px', color: 'var(--ink-3)', fontSize: 12 }}>
            {t('history.noMatch')}
          </div>
        )}
        {visibleRevs.map((r) => (
          <RevRow
            key={r.revision}
            rev={r}
            prev={prevByRev.get(r.revision) ?? null}
            isCurrent={isCurrent(r)}
            isFirstRev={r.revision === 1}
            expanded={expandedRev === r.revision}
            onToggle={() =>
              setExpandedRev(expandedRev === r.revision ? null : r.revision)
            }
            onRestoreRequest={onRestored ? () => setConfirmRestoreRev(r.revision) : undefined}
            restoring={restoringRev === r.revision}
          />
        ))}
      </div>

      {confirmRestoreRev !== null && (
        <ConfirmDialog
          title={
            revs.length > 0 && revs[0].op === 'delete'
              ? t('history.undeleteTitle', { cardId })
              : t('history.restoreTitle', { rev: confirmRestoreRev })
          }
          message={
            revs.length > 0 && revs[0].op === 'delete'
              ? t('history.undeleteMessage')
              : t('history.restoreMessage', { cardId })
          }
          confirmLabel={
            revs.length > 0 && revs[0].op === 'delete'
              ? t('history.undeleteConfirm')
              : t('history.restoreConfirm')
          }
          onConfirm={() => onRestore(confirmRestoreRev)}
          onCancel={() => setConfirmRestoreRev(null)}
        />
      )}
    </div>
  );
}

// ── one row per revision ──────────────────────────────────────────

function RevRow({
  rev,
  prev,
  isCurrent,
  isFirstRev,
  expanded,
  onToggle,
  onRestoreRequest,
  restoring,
}: {
  rev: CardHistoryRevision;
  prev: CardHistoryRevision | null;
  isCurrent: boolean;
  isFirstRev: boolean;
  expanded: boolean;
  onToggle: () => void;
  onRestoreRequest?: () => void;
  restoring: boolean;
}) {
  const { t } = useTranslation();
  const opLabel = OP_LABEL[rev.op] ? t(OP_LABEL[rev.op]) : rev.op;
  const opColor = OP_COLOR[rev.op] ?? 'var(--ink-2)';
  const authorLabel =
    rev.author === 'agent'
      ? t('history.byAgent')
      : rev.author === 'user'
        ? t('history.byYou')
        : t('history.byFs');
  const authorColor =
    rev.author === 'user' ? 'var(--blue)' : rev.author === 'fs' ? 'var(--ink-3)' : 'var(--ink-3)';

  return (
    <article
      style={{
        margin: '0 12px 8px',
        padding: '10px 12px',
        background: isCurrent ? 'var(--paper-1)' : 'white',
        border: '1px solid var(--rule)',
        borderRadius: 5,
      }}
    >
      <button
        onClick={onToggle}
        style={{
          all: 'unset',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          width: '100%',
        }}
      >
        <span
          className="mono"
          style={{
            fontSize: 10,
            color: 'var(--ink-3)',
            letterSpacing: '.05em',
            minWidth: 38,
          }}
        >
          {t('history.revLabel', { rev: rev.revision })}
        </span>
        <span
          className="mono"
          style={{
            fontSize: 10,
            color: opColor,
            fontWeight: 700,
            letterSpacing: '.08em',
          }}
        >
          {opLabel}
        </span>
        <span
          className="mono"
          style={{ fontSize: 10, color: authorColor, fontWeight: 600, letterSpacing: '.06em' }}
        >
          {authorLabel}
        </span>
        <span className="mono" style={{ fontSize: 10, color: 'var(--ink-3)' }}>
          {formatRelative(rev.created_at)}
        </span>
        {isCurrent && (
          <span
            className="mono"
            style={{
              fontSize: 9.5,
              color: 'var(--ink-0)',
              fontWeight: 700,
              letterSpacing: '.06em',
              marginLeft: 'auto',
            }}
          >
            {t('history.current')}
          </span>
        )}
        {!isCurrent && (
          <span style={{ flex: 1 }} />
        )}
        <span
          className="mono"
          style={{ fontSize: 10, color: 'var(--ink-3)', marginLeft: isCurrent ? 0 : undefined }}
        >
          {expanded ? '▾' : '▸'}
        </span>
      </button>

      {/* Source message snippet — line below the header. Truncated by
          server to ~200 chars; we render it as a quote-styled hint. */}
      {rev.source_message_snippet && (
        <div
          style={{
            marginTop: 6,
            paddingLeft: 46,
            fontSize: 11.5,
            color: 'var(--ink-2)',
            lineHeight: 1.5,
            fontStyle: 'italic',
          }}
        >
          ↳ {rev.source_message_snippet}
          {rev.source_message_snippet.length >= 200 && '…'}
        </div>
      )}

      {/* Delete reason — only meaningful when op=delete. */}
      {rev.op === 'delete' && rev.delete_reason && (
        <div
          style={{
            marginTop: 6,
            paddingLeft: 46,
            fontSize: 11.5,
            color: 'var(--red)',
            lineHeight: 1.5,
          }}
        >
          {rev.delete_reason}
        </div>
      )}

      {/* Pre-history label on rev 1 created by the migration backfill. The
          body shown is current-at-migration-time, not creation-time, so
          we disclose that explicitly to avoid implying we have older data. */}
      {isFirstRev && !rev.source_message_snippet && (
        <div
          style={{
            marginTop: 6,
            paddingLeft: 46,
            fontSize: 11,
            color: 'var(--ink-3)',
            lineHeight: 1.5,
          }}
        >
          {t('history.startsHere')}
        </div>
      )}

      {expanded && (
        <ExpandedBody
          rev={rev}
          prev={prev}
          isCurrent={isCurrent}
          onRestoreRequest={onRestoreRequest}
          restoring={restoring}
        />
      )}
    </article>
  );
}

// ── expanded view inside one revision ─────────────────────────────

function ExpandedBody({
  rev,
  prev,
  isCurrent,
  onRestoreRequest,
  restoring,
}: {
  rev: CardHistoryRevision;
  prev: CardHistoryRevision | null;
  isCurrent: boolean;
  onRestoreRequest?: () => void;
  restoring: boolean;
}) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<'snapshot' | 'diff'>(prev ? 'diff' : 'snapshot');
  // Show Restore on:
  //   - every non-current rev (you might want to roll back to it)
  //   - any op=delete rev (its snapshot is the state at deletion time;
  //     restoring it un-deletes the card)
  // The server route detects whether the cards row exists and dispatches
  // to update_card or restore_deleted_card accordingly.
  const showRestore =
    onRestoreRequest && (rev.op === 'delete' || !isCurrent);

  return (
    <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed var(--rule)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <ToggleChip
          active={mode === 'snapshot'}
          onClick={() => setMode('snapshot')}
          label={t('history.snapshot')}
        />
        <ToggleChip
          active={mode === 'diff'}
          onClick={() => setMode('diff')}
          disabled={!prev}
          label={prev ? t('history.diffVsRev', { rev: prev.revision }) : t('history.diffNoPrior')}
        />
        <span style={{ flex: 1 }} />
        {showRestore && (
          <button
            onClick={onRestoreRequest}
            disabled={restoring}
            className="mono"
            style={{
              fontSize: 10.5,
              padding: '4px 10px',
              border: '1px solid var(--rule)',
              borderRadius: 4,
              background: 'white',
              color: 'var(--ink-1)',
              fontWeight: 600,
              letterSpacing: '.04em',
              cursor: restoring ? 'not-allowed' : 'pointer',
            }}
            title={t('history.restoreTooltip')}
          >
            {restoring ? t('history.restoring') : t('history.restore')}
          </button>
        )}
      </div>

      {mode === 'snapshot' && <Snapshot rev={rev} />}
      {mode === 'diff' && prev && <DiffView prev={prev} curr={rev} />}
    </div>
  );
}

function Snapshot({ rev }: { rev: CardHistoryRevision }) {
  const { t } = useTranslation();
  return (
    <div style={{ fontSize: 12, lineHeight: 1.55, color: 'var(--ink-1)' }}>
      <Field label={t('history.fieldKind')}>{rev.kind ?? <em>{t('history.none')}</em>}</Field>
      <Field label={t('history.fieldTitle')}>{rev.title ?? <em>{t('history.none')}</em>}</Field>
      <Field label={t('history.fieldBody')}>
        <pre
          style={{
            margin: 0,
            padding: '8px 10px',
            background: 'var(--paper-1)',
            border: '1px solid var(--rule)',
            borderRadius: 4,
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 11.5,
            lineHeight: 1.55,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: 'var(--ink-0)',
          }}
        >
          {rev.body ?? <em>{t('history.none')}</em>}
        </pre>
      </Field>
      {rev.pack && <Field label={t('history.fieldPack')}>{rev.pack}</Field>}
    </div>
  );
}

function DiffView({
  prev,
  curr,
}: {
  prev: CardHistoryRevision;
  curr: CardHistoryRevision;
}) {
  const { t } = useTranslation();
  // Field-level header — kind/title changes get callouts above the body
  // diff so they're not lost in a sea of equal lines.
  const kindChanged = prev.kind !== curr.kind;
  const titleChanged = (prev.title ?? '') !== (curr.title ?? '');
  const segs = lineDiff(prev.body ?? '', curr.body ?? '');

  return (
    <div style={{ fontSize: 12, lineHeight: 1.55 }}>
      {kindChanged && (
        <FieldDiff
          label={t('history.fieldKind')}
          before={prev.kind ?? t('history.none')}
          after={curr.kind ?? t('history.none')}
        />
      )}
      {titleChanged && (
        <FieldDiff
          label={t('history.fieldTitle')}
          before={prev.title ?? t('history.none')}
          after={curr.title ?? t('history.none')}
        />
      )}
      <div
        style={{
          marginTop: kindChanged || titleChanged ? 10 : 0,
          padding: '6px 0',
          background: 'var(--paper-1)',
          border: '1px solid var(--rule)',
          borderRadius: 4,
          fontFamily: 'JetBrains Mono, monospace',
          fontSize: 11,
          lineHeight: 1.55,
          maxHeight: 360,
          overflowY: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {segs.map((seg, i) => <DiffSegmentLines key={i} seg={seg} />)}
      </div>
    </div>
  );
}

function DiffSegmentLines({ seg }: { seg: DiffSegment }) {
  const color =
    seg.type === 'add' ? 'var(--green, #059669)' : seg.type === 'remove' ? 'var(--red)' : 'var(--ink-1)';
  const bg =
    seg.type === 'add'
      ? 'rgba(16, 185, 129, 0.08)'
      : seg.type === 'remove'
        ? 'rgba(220, 38, 38, 0.08)'
        : 'transparent';
  const prefix = seg.type === 'add' ? '+ ' : seg.type === 'remove' ? '− ' : '  ';

  return (
    <>
      {seg.lines.map((line, j) => (
        <div
          key={j}
          style={{
            padding: '0 12px',
            background: bg,
            color,
          }}
        >
          {prefix}
          {line === '' ? ' ' : line}
        </div>
      ))}
    </>
  );
}

function FieldDiff({ label, before, after }: { label: string; before: string; after: string }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 8,
        marginBottom: 6,
        fontSize: 11.5,
      }}
    >
      <span
        className="mono"
        style={{ fontSize: 9.5, color: 'var(--ink-3)', letterSpacing: '.1em', fontWeight: 700, minWidth: 50 }}
      >
        {label.toUpperCase()}
      </span>
      <span style={{ color: 'var(--red)', textDecoration: 'line-through' }}>{before}</span>
      <span style={{ color: 'var(--ink-3)' }}>→</span>
      <span style={{ color: 'var(--green, #059669)' }}>{after}</span>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div
        className="mono"
        style={{
          fontSize: 9.5,
          color: 'var(--ink-3)',
          letterSpacing: '.1em',
          fontWeight: 700,
          marginBottom: 3,
        }}
      >
        {label.toUpperCase()}
      </div>
      <div>{children}</div>
    </div>
  );
}

function FilterChip({
  label,
  active,
  onToggle,
  title,
}: {
  label: string;
  active: boolean;
  onToggle: () => void;
  title: string;
}) {
  return (
    <button
      onClick={onToggle}
      title={title}
      className="mono"
      style={{
        fontSize: 10,
        padding: '3px 8px',
        border: '1px solid var(--rule)',
        borderRadius: 999,
        background: active ? 'var(--ink-0)' : 'white',
        color: active ? 'white' : 'var(--ink-2)',
        fontWeight: 600,
        letterSpacing: '.05em',
        cursor: 'pointer',
      }}
    >
      {active ? '✓' : '+'} {label}
    </button>
  );
}

function ToggleChip({
  active,
  onClick,
  label,
  disabled = false,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="mono"
      style={{
        fontSize: 10.5,
        padding: '4px 10px',
        border: '1px solid var(--rule)',
        borderRadius: 4,
        background: active ? 'var(--paper-2, #f5f0e8)' : 'white',
        color: disabled ? 'var(--ink-4)' : 'var(--ink-1)',
        fontWeight: 600,
        letterSpacing: '.04em',
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {label}
    </button>
  );
}

/** Maps each op enum to its i18n key (resolved at render time in RevRow). */
const OP_LABEL: Record<CardHistoryRevision['op'], string> = {
  write: 'history.opWrite',
  patch: 'history.opPatch',
  promote: 'history.opPromote',
  fs_sync: 'history.opFs',
  delete: 'history.opDelete',
};

const OP_COLOR: Record<CardHistoryRevision['op'], string> = {
  write: 'var(--ink-2)',
  patch: 'var(--ink-2)',
  promote: 'var(--blue)',
  fs_sync: 'var(--ink-3)',
  delete: 'var(--red)',
};

/** Render an ISO timestamp as "13:42 · today" / "13:42 · 2 days ago" /
 *  "May 1 · 13:42". Tight enough to fit in a row header. */
function formatRelative(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const dayMs = 24 * 60 * 60 * 1000;
  const diff = (now.getTime() - d.getTime()) / dayMs;
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  if (diff < 1 && d.getDate() === now.getDate()) return `${hh}:${mm}`;
  if (diff < 2) return `${hh}:${mm} · ${i18n.t('history.yesterday')}`;
  if (diff < 7) return `${hh}:${mm} · ${i18n.t('history.daysAgo', { count: Math.floor(diff) })}`;
  const month = d.toLocaleString(i18n.language || 'en-US', { month: 'short', day: 'numeric' });
  return `${month} · ${hh}:${mm}`;
}
